import React, { useState, useEffect } from 'react';
import { Lock, ShieldAlert, Globe, X, Plus } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const VISIBILITY_OPTIONS = [
  { value: 'normal', label: 'Normaal', icon: Globe, description: 'Zichtbaar voor iedereen', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
  { value: 'confidential', label: 'Vertrouwelijk', icon: Lock, description: 'Eigenaren + geselecteerde admins', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  { value: 'restricted', label: 'Strikt vertrouwelijk', icon: ShieldAlert, description: 'Alleen eigenaren', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
];

export function VisibilityBadge({ visibility }) {
  const vis = visibility || 'normal';
  if (vis === 'normal') return null;

  const opt = VISIBILITY_OPTIONS.find(o => o.value === vis);
  if (!opt) return null;
  const Icon = opt.icon;

  return (
    <span
      data-testid={`visibility-badge-${vis}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${opt.bg} ${opt.color}`}
    >
      <Icon className="w-3 h-3" />
      {opt.label}
    </span>
  );
}

export function VisibilitySelector({ value, onChange, grantedUsers, onGrantedUsersChange, userRole, allUsers = [] }) {
  const [newEmail, setNewEmail] = useState('');

  // Admins can only see/set "normal"
  if (userRole !== 'owner') {
    return null;
  }

  const currentOpt = VISIBILITY_OPTIONS.find(o => o.value === (value || 'normal'));

  const handleAddUser = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (grantedUsers?.includes(email)) return;
    onGrantedUsersChange?.([...(grantedUsers || []), email]);
    setNewEmail('');
  };

  const handleRemoveUser = (email) => {
    onGrantedUsersChange?.((grantedUsers || []).filter(e => e !== email));
  };

  const adminUsers = allUsers.filter(u => u.email && u.email !== 'owner@tw1.nl');

  return (
    <div data-testid="visibility-selector" className="space-y-3">
      <Label className="text-sm font-medium text-slate-700">Vertrouwelijkheid</Label>
      <Select value={value || 'normal'} onValueChange={onChange}>
        <SelectTrigger data-testid="visibility-dropdown" className="w-full">
          <SelectValue>
            {currentOpt && (
              <span className="flex items-center gap-2">
                <currentOpt.icon className={`w-4 h-4 ${currentOpt.color}`} />
                {currentOpt.label}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {VISIBILITY_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              <div className="flex items-center gap-2">
                <opt.icon className={`w-4 h-4 ${opt.color}`} />
                <div>
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-slate-500">{opt.description}</div>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value === 'confidential' && (
        <div data-testid="user-access-picker" className="space-y-2 p-3 rounded-lg bg-amber-50/50 border border-amber-100">
          <Label className="text-xs font-medium text-amber-800">Toegang verlenen aan</Label>

          {(grantedUsers || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {grantedUsers.map(email => (
                <Badge
                  key={email}
                  variant="secondary"
                  className="bg-amber-100 text-amber-800 gap-1 pr-1"
                >
                  {email}
                  <button
                    onClick={() => handleRemoveUser(email)}
                    className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
                    data-testid={`remove-user-${email}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1">
              {adminUsers.length > 0 ? (
                <Select
                  value=""
                  onValueChange={(email) => {
                    if (!grantedUsers?.includes(email)) {
                      onGrantedUsersChange?.([...(grantedUsers || []), email]);
                    }
                  }}
                >
                  <SelectTrigger data-testid="add-user-dropdown" className="h-8 text-sm">
                    <SelectValue placeholder="Gebruiker toevoegen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {adminUsers
                      .filter(u => !(grantedUsers || []).includes(u.email))
                      .map(u => (
                        <SelectItem key={u.email} value={u.email}>
                          {u.name || u.email}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-1">
                  <Input
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder="email@voorbeeld.nl"
                    className="h-8 text-sm"
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddUser())}
                    data-testid="add-user-email-input"
                  />
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={handleAddUser}>
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisibilitySelector;
